import React, { FC, useEffect, useState, useRef } from "react";
import {
  ErrorContainer,
  Input,
  PasswordInput,
  Text,
  FilledButton as Button,
  theme,
} from "@ifgengineering/component-library";
import { navigate } from "gatsby";
import { Field, Formik } from "formik";
import { useDispatch } from "react-redux";
import {
  Container,
  Link,
  ForgotPassword,
  OutlineButtonWithImage,
} from "../styled";
import * as Yup from "yup";
import MagicCode from "../MagicCode";
import Icon from "@icon-park/react/es/all";
import { useClientAuthComponents } from "../../../Context";
import { Turnstile, TurnstileInstance } from "@marsidev/react-turnstile";
import { SigninFieldType, SigninProps } from "./types";
import GoogleIcon from "../../../../../assets/googleIcon.png";
import AppleIcon from "../../../../../assets/appleIcon.png";
import AppleLogin from "react-apple-login";
import { toast } from "react-toastify";
import { TwoFactorAuthMethod } from "@ifgengineering/client-auth-sdk";

const Signin: FC<SigninProps> = ({
  googleLogin,
  appleLogin,
  forgotPasswordURL,
  loginRedirect,
  onSubmit,
  signupURL,
  title,
  errorMessage,
  isLoading,
  turnstile,
}) => {
  const {
    useAnalytics,
    loginAction,
    is2FAEnabled,
    turnstileSiteKey,
    getTwoFactorAuthDetails,
    generateOTP,
    logException,
    setIs2FAFlow,
    setEmailPassword,
  } = useClientAuthComponents();

  const [loading, setLoading] = useState(false);
  const [showMagicCodeForm, setShowMagicCodeForm] = useState(false);
  const [userEmail, setUserEmail] = useState<string>("");
  const firePageViewAnalytics = useAnalytics("pageView");
  const turnstileRef = useRef<TurnstileInstance | null>(null);
  const [twoFAMethod, setTwoFAMethod] = useState<TwoFactorAuthMethod>("email");
  const [twoFAErrorMessage, setTwoFAErrorMessage] = useState("");
  const isTurnstileEnabled = turnstile?.enabled || false;

  useEffect(() => {
    return () => {
      if (typeof document === "undefined") {
        return;
      }
      document.body.removeAttribute("data-turnstile-verified");
    };
  }, []);

  const SigninSchema = Yup.object().shape({
    password: Yup.string()
      .min(8, "Must be at least 8 characters")
      .max(64, "Please enter a password less than 64 characters")
      .required("Password is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    captchaToken: isTurnstileEnabled
      ? Yup.string().required("Please verify you are not a robot")
      : Yup.string().optional(),
  });

  useEffect(() => {
    const firePageView = async (eventName: string) => {
      (await firePageViewAnalytics)({
        eventName,
      });
    };
    firePageView(`all:login_pageViewed`);
  }, []);

  const fireAnalytics = useAnalytics("login");
  const dispatch = useDispatch();

  const handleSubmit = async (values: {
    email: string;
    password: string;
    captchaToken: string;
  }) => {
    if (loading) return; // Prevent double submission

    setLoading(true);

    if (onSubmit) {
      try {
        await onSubmit({
          email: values.email.toLowerCase(),
          password: values.password,
          captchaToken: isTurnstileEnabled ? values.captchaToken : "",
        });
        (await fireAnalytics)({
          email: values.email.toLowerCase(),
          method: "password",
        });
      } finally {
        setLoading(false);
      }
      return;
    }

    setTwoFAErrorMessage("");

    try {
      const twoFADetails = await getTwoFactorAuthDetails?.({
        email: values.email.toLowerCase(),
        password: values.password,
        captchaToken: isTurnstileEnabled ? values.captchaToken : "",
      });

      if (twoFADetails?.error) {
        if (twoFADetails.error.statusCode === 429) {
          setTwoFAErrorMessage(twoFADetails.error.message);
          return;
        }

        if (twoFADetails.error.response) {
          if (twoFADetails.error.response.status === 422) {
            setTwoFAErrorMessage("Please set a new password");
            return;
          }

          if (twoFADetails?.error.response.status === 401) {
            setTwoFAErrorMessage("Invalid email or password");
            return;
          }
        }

        logException({
          email: values.email.toLowerCase(),
          tag: "getTwoFactorAuthDetails",
          exception:
            twoFADetails?.error.message || JSON.stringify(twoFADetails?.error),
        });

        setTwoFAErrorMessage(
          twoFADetails?.error.message || JSON.stringify(twoFADetails?.error)
        );
        return;
      }

      if (
        is2FAEnabled &&
        twoFADetails?.isTwoFactorAuthEnabled &&
        twoFADetails?.primaryTwoFactorAuthMethod
      ) {
        setIs2FAFlow(true);
        setTwoFAMethod(twoFADetails?.primaryTwoFactorAuthMethod);
        const emailLowercase = values.email.toLowerCase();
        setUserEmail(emailLowercase);
        setEmailPassword({
          email: emailLowercase,
          password: values.password,
        });
        setShowMagicCodeForm(true);
        generateOTP(emailLowercase);
        return;
      }

      const response = await dispatch(
        loginAction({
          email: values.email.toLowerCase(),
          password: values.password,
          captchaToken: isTurnstileEnabled ? values.captchaToken : "",
        })
      );

      if (response?.payload) {
        (await fireAnalytics)({
          email: values.email.toLowerCase(),
          method: "password",
        });
        navigate(loginRedirect);
      }
    } finally {
      setLoading(false);
    }
  };

  const fireForgotPasswordAnalytics = useAnalytics("forogtPasswordClick");
  const isSocialLoginEnabled = googleLogin?.enable || appleLogin?.enable;

  const regularLoginInput = (
    <Formik
      enableReinitialize
      validationSchema={SigninSchema}
      initialValues={{
        email: "",
        password: "",
        captchaToken: "",
      }}
      onSubmit={handleSubmit}
    >
      {({ setFieldValue, submitForm, errors, touched }) => {
        const isEmailInvalid = errors.email && touched.email;
        const isPasswordInvalid = errors.password && touched.password;
        const isCaptchaTokeInvalid =
          errors.captchaToken && touched.captchaToken;
        const shouldDisableLoginBtn =
          isEmailInvalid ||
          isPasswordInvalid ||
          isCaptchaTokeInvalid ||
          isLoading;

        return (
          <Container padding="24px" gap={16}>
            <Text type="T18" color="SLATE800" fontFamily="archiasemibold">
              {title || "Login"}
            </Text>

            <Container gap={16}>
              <Field name="email">
                {({ field, form: { errors, touched } }: SigninFieldType) => (
                  <Input
                    height="auto"
                    {...field}
                    onChange={(e) => setFieldValue("email", e.target.value)}
                    errorMessage={touched.email ? errors.email : undefined}
                    heading="Email"
                    testId="email-input"
                  />
                )}
              </Field>
              <Container gap={8}>
                <Field name="password">
                  {({ field, form: { errors, touched } }: SigninFieldType) => (
                    <PasswordInput
                      {...field}
                      errorMessage={
                        touched.password ? errors.password : undefined
                      }
                      onChange={(value) => setFieldValue("password", value)}
                      heading="Password"
                      hideBar
                    />
                  )}
                </Field>

                <Container
                  direction="row"
                  alignItems="flex-start"
                  justifyContent="space-between"
                >
                  <div style={{ flex: 1 }}>
                    {(errorMessage || twoFAErrorMessage) && (
                      <ErrorContainer style={{ marginBottom: 8 }}>
                        <Icon
                          type="Caution"
                          size={14}
                          fill={theme.colors.ERROR800}
                          theme="filled"
                        />
                        <Text type="P14" color="ERROR800">
                          {errorMessage || twoFAErrorMessage}
                        </Text>
                      </ErrorContainer>
                    )}
                  </div>
                  <ForgotPassword
                    onClick={async () => {
                      (await fireForgotPasswordAnalytics)();
                      navigate(forgotPasswordURL || "/auth/forgot-password");
                    }}
                  >
                    Reset Password
                  </ForgotPassword>
                </Container>
              </Container>
              {isTurnstileEnabled && (
                <Field name="captchaToken">
                  {({ form: { errors, touched } }: SigninFieldType) => (
                    <div data-testid="turnstile-widget">
                      <Turnstile
                        ref={turnstileRef}
                        siteKey={turnstileSiteKey}
                        onSuccess={(token) => {
                          setFieldValue("captchaToken", token);
                          // Add detectable marker for E2E tests
                          document.body.setAttribute(
                            "data-turnstile-verified",
                            "true"
                          );
                        }}
                        onError={() => {
                          setFieldValue("captchaToken", "");
                          document.body.removeAttribute(
                            "data-turnstile-verified"
                          );
                        }}
                        onExpire={() => {
                          setFieldValue("captchaToken", "");
                          document.body.removeAttribute(
                            "data-turnstile-verified"
                          );
                        }}
                        options={{
                          theme: "light",
                          size: "flexible",
                        }}
                      />
                      {touched.captchaToken && errors.captchaToken && (
                        <ErrorContainer>
                          <Icon
                            type="Caution"
                            size={14}
                            fill={theme.colors.ERROR800}
                            theme="filled"
                          />
                          <Text type="P14" color="ERROR800">
                            {errors.captchaToken}
                          </Text>
                        </ErrorContainer>
                      )}
                    </div>
                  )}
                </Field>
              )}
            </Container>

            <Container gap={8} alignItems="center">
              <Button
                testId="login-button"
                height="56px"
                text="Log in"
                color="blue"
                onClick={submitForm}
                isLoading={isLoading || loading}
                disabled={shouldDisableLoginBtn}
                borderRadius="8px"
              />
              {googleLogin?.enable && (
                <OutlineButtonWithImage
                  text="Continue with Google"
                  image={GoogleIcon}
                  testId="google-login-button"
                  onClick={googleLogin?.onClick}
                  type="button"
                />
              )}

              {appleLogin?.enable && (
                <AppleLogin
                  clientId={appleLogin.clientId}
                  redirectURI={appleLogin.redirectURI}
                  usePopup={true}
                  responseMode="form_post"
                  responseType="code"
                  scope="email"
                  render={({ onClick }) => (
                    <OutlineButtonWithImage
                      text="Continue with Apple"
                      image={AppleIcon}
                      testId="apple-login-button"
                      onClick={onClick}
                      type="button"
                    />
                  )}
                  callback={(data) => {
                    if ("error" in data) {
                      const code =
                        typeof data.error === "string"
                          ? data.error
                          : data.error?.error;
                      if (
                        code === "user_cancelled_authorize" ||
                        code === "popup_closed_by_user"
                      ) {
                        return;
                      }
                      toast.error("Error authenticating with Apple.");
                      logException({
                        tag: "appleLogin",
                        exception:
                          typeof data.error === "string"
                            ? data.error
                            : JSON.stringify(data.error),
                      });
                      return;
                    }
                    if (data) {
                      appleLogin.onClick && appleLogin?.onClick(data);
                    }
                  }}
                />
              )}
              <div>
                <Text type="S14" color="SLATE700">
                  New here?
                </Text>{" "}
                <Link
                  onClick={() => navigate(signupURL || "/auth/signup")}
                  fontSize="14px"
                >
                  Sign Up Now
                </Link>
              </div>
            </Container>
          </Container>
        );
      }}
    </Formik>
  );

  return (
    <Container>
      {showMagicCodeForm ? (
        <MagicCode
          handleCancel={() => setShowMagicCodeForm(false)}
          loginRedirect={loginRedirect}
          method={twoFAMethod}
          customErrorMessage={twoFAErrorMessage}
          email={userEmail}
        />
      ) : (
        regularLoginInput
      )}
    </Container>
  );
};

export default Signin;
